import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { DeleteOutlined, LeftOutlined, PlusOutlined, RightOutlined, TeamOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useTokens } from "../../../core/theme/TokensProvider";
import { Alert } from "../../../ui/primitives/Alert";
import { Button } from "../../../ui/primitives/Button";
import { Card } from "../../../ui/primitives/Card";
import { Collapse } from "../../../ui/primitives/Collapse";
import { EmptyState } from "../../../ui/primitives/EmptyState";
import { Stack } from "../../../ui/primitives/Stack";
import { Modal } from "../../../ui/primitives/Modal";
import { Select } from "../../../ui/primitives/Select";
import { Tag } from "../../../ui/primitives/Tag";
import { Text } from "../../../ui/primitives/Text";
import { Tooltip } from "../../../ui/primitives/Tooltip";
import { useRotaService } from "../../../services/members/RotaServiceProvider";
import {
  Allocation,
  AllocationKind,
  RosterStaffMember,
  RotaSlot,
  StaffWeeklyHours,
} from "../../../model/members/Rota";
import {
  allocationLabel,
  callLabel,
  formatShiftWindow,
  groupWorkForShift,
  shiftTypeFor,
  slotsForDay,
} from "../../../utils/rotaUtils";
import AddWorkModal, { AddWorkTarget } from "./AddWorkModal";
import ManageStaffDrawer from "./ManageStaffDrawer";

dayjs.extend(isoWeek);

const ALLOCATION_TONES: Record<AllocationKind, "primary" | "warning" | "success"> = {
  Task: "primary",
  Call: "warning",
  BizDev: "success",
};

// "Business Development" in full: it is the category name the club uses, and the widget shows
// the category followed by the title, so this is the word staff actually read (FORT-351).
const ALLOCATION_LABELS: Record<AllocationKind, string> = {
  Task: "Task",
  Call: "Call",
  BizDev: "Business Development",
};

// AntD gives every Tag a trailing margin of its own, which double-spaces it against the
// Stack's gap. Zeroed wherever a Tag sits inside a Stack on this screen, so chips are
// spaced by the token scale alone and every row uses the same gap.
const TAG_IN_STACK: React.CSSProperties = { flexShrink: 0, marginInlineEnd: 0 };

// The allocation category tag specifically. Unlike the short tags above it must be allowed to
// give up width: "Business Development" is ~145px, and at the grid's minmax(180px, 1fr) floor
// the row is only ~136px — with flexShrink: 0 the tag pushes the remove control clean outside
// the shift card on a 1366px laptop. It wraps inside the pill rather than ellipsising, because
// the category is the thing being scanned for and half a category is no use.
const CATEGORY_TAG: React.CSSProperties = {
  marginInlineEnd: 0,
  minWidth: 0,
  whiteSpace: "normal",
};

// Used by the call rows, which are still a single line: the label absorbs the overflow so
// the remove control keeps its place. `overflowWrap` because a day column is 180px and one
// unbroken token (a long focus area, a membership id) would otherwise push the card
// sideways rather than break.
const ROW_TEXT: React.CSSProperties = { flex: 1, minWidth: 0, overflowWrap: "anywhere" };

// An allocation's label sits on its own line, so it takes the full column rather than
// competing with the category tag. Same overflowWrap for the same reason.
const LABEL_TEXT: React.CSSProperties = { minWidth: 0, overflowWrap: "anywhere" };

/**
 * Work Allocation — a column per day, and within each column one widget per shift showing
 * who is working and when. The rota is visible right here, so the manager can see who is on
 * shift and decide what to allocate without leaving the page.
 *
 * The rota itself (which shifts exist, who covers them) is a recurring weekly pattern
 * edited only in Manage Staff; this page is about the work allocated onto those shifts,
 * which *is* date-specific — hence the week navigation.
 */
/**
 * FORT-370: the staff member actually working an occurrence — the coverer if the shift is
 * covered, otherwise whoever is rostered. The API sends `effectiveStaffId` precomputed; the
 * fallback covers the dateless pattern read, which has no cover information at all.
 */
const holderOf = (slot: RotaSlot): number | null => slot.effectiveStaffId ?? slot.staffId;

/**
 * FORT-375: when a piece of work was signed off.
 *
 * completed_at is a TIMESTAMPTZ — a real instant, not a calendar date — so it is read with
 * plain dayjs in the browser's own zone. Running it through the date-only helpers would
 * rebuild it from UTC calendar fields and show an evening sign-off on the wrong day.
 * Weekday + time, because within a single week the day is what locates it.
 *
 * Known limitation: the SERVER windows the week in the club's timezone while this renders in
 * the BROWSER's. They agree for anyone working at or near the club — the only case the client
 * has — but a manager in another zone could see a boundary sign-off labelled with a day that
 * sits outside the week named above it. Formatting in locale.timezone would close that.
 */
const formatCompletedAt = (value: string | Date | null | undefined): string => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("ddd HH:mm") : "";
};

const WorkAllocationRota: React.FC = () => {
  const service = useRotaService();
  const tokens = useTokens();

  const [weekStart, setWeekStart] = useState<Dayjs>(dayjs().startOf("isoWeek"));
  const [slots, setSlots] = useState<RotaSlot[]>([]);
  const [roster, setRoster] = useState<RosterStaffMember[]>([]);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [addWork, setAddWork] = useState<AddWorkTarget | null>(null);
  // FORT-370: which occurrence is being covered, or null. Keyed by slot AND date because
  // the rota pattern has no dates of its own — cover applies to one day, not the shift
  // for all time.
  const [covering, setCovering] = useState<AddWorkTarget | null>(null);
  // FORT-371: rostered hours per staff member for the week on screen.
  const [hours, setHours] = useState<StaffWeeklyHours[]>([]);
  // FORT-375: what was finished this week. Separate from `allocations`, which is
  // outstanding work only — the two never overlap, because a completed item leaves that read
  // the moment it is signed off.
  const [completed, setCompleted] = useState<Allocation[]>([]);
  // Whether the completed read FAILED, as distinct from returning nothing. Without the
  // distinction a 500 renders as "nothing was signed off this week", which is a false
  // statement about the team's week rather than a missing panel.
  const [completedError, setCompletedError] = useState(false);

  const weekStartStr = weekStart.format("YYYY-MM-DD");

  // Only reception staff can be rostered, so only they can cover — the same rule the API
  // enforces, mirrored here so the picker doesn't offer someone the write would reject.
  const coverCandidates = useMemo(
    () => roster.filter((r) => r.isActive && r.role === "Staff").map((r) => ({ label: r.name, value: r.id })),
    [roster]
  );
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day")), [weekStart]);

  const load = useCallback(async () => {
    setError(null);
    // Cleared up front, not just on the success path: if a SIBLING read rejects, control jumps
    // to the catch and the panel would otherwise keep the previous week's rows under a label
    // that already names the new one.
    setCompleted([]);
    setCompletedError(false);
    try {
      const [slotList, hoursList, rosterList, allocationList, completedList] = await Promise.all([
        service.getSlots(weekStartStr),
        // Best-effort by construction: unlike getSlots/getRoster this one doesn't use
        // required(), so a failed read resolves to [] and the strip simply doesn't render
        // rather than taking the whole rota down with it.
        service.getWeeklyHours(weekStartStr),
        service.getRoster(),
        service.getWeekAllocations(weekStartStr),
        // Best-effort for the GRID — a failed review must not take the planning view down —
        // but the failure is remembered and shown inside the panel rather than swallowed.
        service.getCompletedWork(weekStartStr).catch(() => null),
      ]);
      setSlots(slotList);
      setHours(hoursList);
      setRoster(rosterList);
      setAllocations(allocationList);
      setCompletedError(completedList == null);
      setCompleted(completedList ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load the rota.");
    }
  }, [service, weekStartStr]);

  useEffect(() => {
    load();
  }, [load]);

  // Only active Staff-type members can be rostered — Management do the allocating, so they are
  // never someone to allocate work to.
  const receptionStaff = useMemo(
    () => roster.filter((s) => s.isActive && s.role === "Staff"),
    [roster]
  );

  /**
   * Completed work grouped by whoever signed it off, each person's items newest first.
   *
   * Falls back to the assignee's name when the API has no completing staff member — true of
   * rows completed before the audit columns existed, which V67 backfills to the assignee
   * anyway, and of any row whose completer has since been removed from the roster.
   */
  const completedByStaff = useMemo(() => {
    const groups = new Map<string, Allocation[]>();
    completed.forEach((a) => {
      const who = a.completedByStaffName ?? a.staffName;
      const bucket = groups.get(who);
      if (bucket) bucket.push(a);
      else groups.set(who, [a]);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [completed]);

  const allocationKey = (a: Allocation) => `${a.kind}-${a.id}`;

  /**
   * Lays one day's allocations onto that day's shifts.
   *
   * Each allocation is claimed by at most one slot (the earliest that person covers), so
   * someone working two shifts in a day doesn't see their work rendered twice. Anything
   * left over — work assigned to a manager via the CRM, or orphaned when a shift was
   * deleted or reassigned — is returned separately rather than silently dropped: an
   * invisible allocation is also an unremovable one.
   */
  const planDay = useCallback(
    (dateStr: string, daySlots: RotaSlot[]) => {
      const dayAllocations = allocations.filter((a) => a.date === dateStr);
      const claimed = new Set<string>();
      const bySlot = new Map<number, Allocation[]>();

      daySlots.forEach((slot) => {
        // FORT-370: the person ACTUALLY on the shift, not the one rostered. A covered
        // shift showing the coverer's name but the covered person's tasks would be worse
        // than no cover feature at all — the coverer's own work would fall into
        // "Not on a shift" while they stood there holding someone else's list.
        const holder = holderOf(slot);
        if (holder == null) {
          bySlot.set(slot.id, []);
          return;
        }
        const mine = dayAllocations.filter(
          (a) => a.staffId === holder && !claimed.has(allocationKey(a))
        );
        mine.forEach((a) => claimed.add(allocationKey(a)));
        bySlot.set(slot.id, mine);
      });

      return { bySlot, unscheduled: dayAllocations.filter((a) => !claimed.has(allocationKey(a))) };
    },
    [allocations]
  );

  const removeCover = useCallback(
    async (slotId: number, dateStr: string) => {
      try {
        await service.removeCover(slotId, dateStr);
        await load();
      } catch {
        // The API client already surfaces the failure as a notification.
      }
    },
    [service, load]
  );

  const saveCover = useCallback(
    async (staffId: number) => {
      if (covering == null) return;
      try {
        await service.coverSlot(covering.slot.id, covering.date, staffId);
        setCovering(null);
        await load();
      } catch {
        // The API client already surfaces the failure as a notification.
      }
    },
    [service, covering, load]
  );

  const removeAllocation = useCallback(
    async (allocation: Allocation) => {
      try {
        if (allocation.kind === "BizDev") await service.deleteBizDevAllocation(allocation.id);
        else await service.deleteTaskAllocation(allocation.id);
        await load();
      } catch {
        message.error("Failed to remove work");
      }
    },
    [service, load]
  );

  /**
   * The remove control. Icon-only and fixed-width so it occupies the same space on every
   * row: a "Remove" text link is wide enough that in a day column it fits beside a short
   * title and wraps below a long one, which is why rows used to line up differently.
   *
   * The tooltip is the sighted affordance; aria-label is the accessible name, and it names
   * the row so it isn't one of several identical "Remove" buttons to a screen reader.
   */
  const renderRemove = (a: Allocation, label: string) => (
    <Tooltip title="Remove">
      <Button
        variant="link"
        size="sm"
        icon={<DeleteOutlined />}
        aria-label={`Remove ${label}`}
        data-testid={`rota-allocation-remove-${a.kind}-${a.id}`}
        onClick={() => removeAllocation(a)}
      />
    </Tooltip>
  );

  /**
   * One call inside the Sales Calls group. Deliberately lighter than a full allocation row:
   * the group heading already says these are calls, so the row is just who to ring.
   *
   * `withAssignee` is for the off-shift bucket, which holds calls for *several* people at
   * once — there the name of whoever has to make the call is the point, whereas inside a
   * shift widget it's already in the header.
   */
  const renderCall = (a: Allocation, withAssignee = false) => {
    const label = withAssignee ? `${a.staffName} — ${callLabel(a)}` : callLabel(a);
    return (
      <Stack
        key={allocationKey(a)}
        direction="horizontal"
        gap={1}
        align="flex-start"
        data-testid={`rota-call-${a.id}`}
      >
        <Text variant="caption" style={ROW_TEXT}>
          {label}
        </Text>
        {renderRemove(a, label)}
      </Stack>
    );
  };

  /**
   * The calls for one shift, behind a single collapsed heading carrying the outstanding
   * count. Collapsed by default: a busy day's calls otherwise run to dozens of rows and
   * bury the one-off tasks in the same widget.
   *
   * The count is of *outstanding* calls because the week query returns only open ones —
   * completed and cancelled never reach here.
   */
  const renderCallGroup = (slotKey: string, calls: Allocation[], withAssignee = false) => (
    <Collapse
      size="small"
      bordered={false}
      data-testid={`rota-calls-group-${slotKey}`}
      items={[
        {
          key: "calls",
          label: (
            <Stack direction="horizontal" gap={1} align="center">
              <Tag tone={ALLOCATION_TONES.Call} style={TAG_IN_STACK}>
                Sales Calls
              </Tag>
              <Tag tone="default" style={TAG_IN_STACK} data-testid={`rota-calls-count-${slotKey}`}>
                {calls.length}
              </Tag>
            </Stack>
          ),
          children: <Stack gap={1}>{calls.map((c) => renderCall(c, withAssignee))}</Stack>,
        },
      ]}
    />
  );

  /**
   * One allocation: category and remove on the first line, what it is on the second.
   *
   * These used to share a single flex line. That worked while the category tag was short,
   * but the tag carries flexShrink: 0 and a day column is only minmax(180px, 1fr) — so once
   * "Biz dev" became "Business Development" (FORT-351) the tag consumed the whole column,
   * the label was squeezed to a few pixels, and ROW_TEXT's `overflowWrap: anywhere` broke it
   * at every character. The result read as a vertical column of letters.
   *
   * Stacking is the fix rather than letting the tag shrink: an ellipsised category is
   * unreadable, and the category is the thing being scanned for. Giving the label the full
   * column width also gives an unbroken task title somewhere to wrap.
   */
  const renderAllocation = (a: Allocation) => {
    const label = allocationLabel(a);
    return (
      <Stack key={allocationKey(a)} gap={0} data-testid={`rota-allocation-${a.kind}-${a.id}`}>
        <Stack direction="horizontal" gap={1} align="center" justify="space-between">
          <Tag tone={ALLOCATION_TONES[a.kind]} style={CATEGORY_TAG}>
            {ALLOCATION_LABELS[a.kind]}
          </Tag>
          {renderRemove(a, label)}
        </Stack>
        <Text variant="caption" style={LABEL_TEXT}>
          {label}
        </Text>
      </Stack>
    );
  };

  const renderShift = (slot: RotaSlot, date: Dayjs, work: Allocation[]) => {
    const dateStr = date.format("YYYY-MM-DD");
    // Who is actually on this shift — the coverer when there is one. Everything that asks
    // "whose shift is this" goes through here so the three call sites can't drift.
    const staffId = holderOf(slot);
    const { individual, calls } = groupWorkForShift(work);
    const slotKey = `${slot.id}-${dateStr}`;

    return (
      <div
        key={slot.id}
        data-testid={`rota-shift-${slot.id}-${dateStr}`}
        style={{
          background: tokens.color.background.muted,
          border: `1px solid ${tokens.color.border.default}`,
          borderRadius: tokens.radius.md,
          padding: tokens.space[2],
        }}
      >
        <Stack gap={1}>
          <Tag tone="default" style={TAG_IN_STACK}>
            {shiftTypeFor(slot)}
          </Tag>
          <Text weight="semibold" color={staffId == null ? "disabled" : "primary"}>
            {slot.isCovered ? slot.coveringStaffName : slot.staffName ?? "Unassigned"}
          </Text>
          {/*
            FORT-370: named as a cover, not a reassignment. Showing only the coverer would
            read as a permanent change to the rota — which is exactly what the client said
            they don't want — so the person being covered stays visible underneath.
          */}
          {slot.isCovered ? (
            <Tag tone="warning" style={TAG_IN_STACK} data-testid={`rota-cover-tag-${slot.id}-${dateStr}`}>
              covering {slot.staffName ?? "an empty shift"}
            </Tag>
          ) : null}
          <Text variant="caption">{formatShiftWindow(slot)}</Text>

          {individual.map(renderAllocation)}

          {calls.length > 0 ? renderCallGroup(slotKey, calls) : null}

          {holderOf(slot) == null ? (
            <Text variant="caption" color="disabled">
              Use Manage staff to assign someone
            </Text>
          ) : (
            <Button
              variant="link"
              size="sm"
              icon={<PlusOutlined />}
              data-testid={`rota-add-work-${slot.id}-${dateStr}`}
              onClick={() => setAddWork({ slot, date: dateStr })}
            >
              Add work
            </Button>
          )}

          {/*
            Cover is per-DATE, so it lives on the day cell rather than on the shift in the
            abstract. Removing it is the revert — there is no "restore" action because
            deleting the override is what makes the base pattern show through again.
          */}
          {slot.isCovered ? (
            <Button
              variant="link"
              size="sm"
              data-testid={`rota-remove-cover-${slot.id}-${dateStr}`}
              onClick={() => removeCover(slot.id, dateStr)}
            >
              Remove cover
            </Button>
          ) : (
            <Button
              variant="link"
              size="sm"
              data-testid={`rota-cover-${slot.id}-${dateStr}`}
              onClick={() => setCovering({ slot, date: dateStr })}
            >
              Cover this shift
            </Button>
          )}
        </Stack>
      </div>
    );
  };

  return (
    <div data-testid="work-allocation-rota" style={{ padding: tokens.space[5] }}>
      <Stack
        direction="horizontal"
        justify="space-between"
        align="center"
        wrap
        style={{ marginBottom: tokens.space[4] }}
      >
        <Stack gap={0}>
          <Text variant="h3">Work Allocation</Text>
          <Text color="secondary">
            Week of {weekStart.format("D MMM")} — click a shift to allocate that person's work for the day
          </Text>
        </Stack>
        <Stack direction="horizontal" gap={2} align="center" wrap>
          <Button data-testid="rota-week-prev" icon={<LeftOutlined />} onClick={() => setWeekStart((w) => w.subtract(7, "day"))}>
            Previous
          </Button>
          <Button data-testid="rota-week-today" onClick={() => setWeekStart(dayjs().startOf("isoWeek"))}>
            This week
          </Button>
          <Button data-testid="rota-week-next" icon={<RightOutlined />} onClick={() => setWeekStart((w) => w.add(7, "day"))}>
            Next
          </Button>
          <Button icon={<TeamOutlined />} data-testid="manage-staff-btn" onClick={() => setDrawerOpen(true)}>
            Manage staff
          </Button>
        </Stack>
      </Stack>

      {/*
        FORT-371: the week's rostered hours per person, above the grid — the client asked
        for this to "make payroll much easier", and payroll is read at a glance rather than
        by adding up seven columns by eye.

        ROSTERED, not attended: nothing here reconciles against attendance, so it says
        "rostered" on the label rather than implying it is a record of work done.
      */}
      {hours.length > 0 ? (
        <Card data-testid="rota-weekly-hours" size="small" style={{ marginBottom: tokens.space[4] }}>
          <Stack gap={2}>
            {/*
              Labelled with the week on screen, not "this week" — Previous/Next reload it,
              and historical totals under a "this week" heading is a misread waiting to
              happen on a payroll readout.
            */}
            <Text weight="semibold">
              Rostered hours — week of {weekStart.format("D MMM")}
            </Text>
            <Text variant="caption" color="secondary">
              As the rota stands today. Rostered, not attended — a payroll input, not a record of work done.
            </Text>
            <Stack direction="horizontal" gap={4} wrap>
              {hours.map((h) => (
                <Stack gap={0} key={h.staffId} data-testid={`rota-hours-${h.staffId}`}>
                  <Text variant="caption" color="secondary">
                    {h.staffName}
                  </Text>
                  <Text weight="semibold">{h.formatted}</Text>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Card>
      ) : null}

      {error ? (
        <Alert type="error" message={error} style={{ marginBottom: tokens.space[4] }} data-testid="rota-error" />
      ) : null}

      {slots.length === 0 ? (
        <EmptyState
          data-testid="rota-no-shifts"
          description="No shifts on the rota yet. Add the club's weekly shifts in Manage staff — they then repeat every week until you change them."
          action={
            <Button variant="primary" icon={<TeamOutlined />} onClick={() => setDrawerOpen(true)}>
              Manage staff
            </Button>
          }
        />
      ) : (
        <div
          data-testid="rota-day-columns"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: tokens.space[3],
            alignItems: "start",
          }}
        >
          {days.map((date) => {
            const dateStr = date.format("YYYY-MM-DD");
            const daySlots = slotsForDay(slots, date.isoWeekday() - 1);
            const { bySlot, unscheduled } = planDay(dateStr, daySlots);
            const offShift = groupWorkForShift(unscheduled);
            const isToday = date.isSame(dayjs(), "day");
            return (
              <div
                key={dateStr}
                data-testid={`rota-day-${dateStr}`}
                style={{
                  background: tokens.color.background.default,
                  border: `1px solid ${isToday ? tokens.color.primary : tokens.color.border.default}`,
                  borderRadius: tokens.radius.lg,
                  padding: tokens.space[3],
                }}
              >
                <Stack gap={2}>
                  <Text variant="h6">{date.format("ddd D MMM")}</Text>
                  {daySlots.length === 0 ? (
                    <Text variant="caption" color="disabled">
                      No shifts
                    </Text>
                  ) : (
                    daySlots.map((slot) => renderShift(slot, date, bySlot.get(slot.id) ?? []))
                  )}

                  {unscheduled.length > 0 ? (
                    <div data-testid={`rota-unscheduled-${dateStr}`}>
                      <Stack gap={1}>
                        <Text variant="caption" color="warning">
                          Not on a shift
                        </Text>
                        {/* Grouped the same way as a shift's work — a batch of calls assigned
                            to someone who isn't rostered that day is exactly the case that
                            produced the longest lists. */}
                        {offShift.individual.map(renderAllocation)}
                        {offShift.calls.length > 0
                          ? renderCallGroup(`unscheduled-${dateStr}`, offShift.calls, true)
                          : null}
                      </Stack>
                    </div>
                  ) : null}
                </Stack>
              </div>
            );
          })}
        </div>
      )}

      {/*
        FORT-375: what the team actually finished this week.

        Below the grid and collapsed by default — this is a review, and the planning grid is
        the job. Grouped by staff member rather than by day because the client's ask was
        "what was completed and who completed it", which reads person-first, and it mirrors
        how the work was allocated in the first place.
      */}
      <div style={{ marginTop: tokens.space[4] }}>
        <Collapse
          data-testid="rota-completed"
          items={[
            {
              key: "completed",
              // Named by the week on screen, not "this week": the panel follows the week
              // navigator, so after stepping back it would otherwise mislabel a past week.
              label: completedError
                ? `Completed — week of ${weekStart.format("D MMM")} (unavailable)`
                : `Completed — week of ${weekStart.format("D MMM")} (${completed.length})`,
              children: completedError ? (
                <Alert
                  type="error"
                  message="Couldn't load completed work for this week. Try reloading."
                  data-testid="rota-completed-error"
                />
              ) : completed.length === 0 ? (
                  <EmptyState
                    description="Nothing signed off in this week. Work appears here once the person it was allocated to signs it off."
                    data-testid="rota-completed-empty"
                  />
                ) : (
                  <Stack gap={3}>
                    {completedByStaff.map(([staffName, items]) => (
                      <div key={staffName} data-testid={`rota-completed-staff-${staffName}`}>
                        <Text variant="h4">{staffName}</Text>
                        <Stack gap={1} style={{ marginTop: tokens.space[1] }}>
                          {items.map((a) => (
                            <Stack
                              key={`${a.kind}-${a.id}`}
                              direction="horizontal"
                              gap={2}
                              align="flex-start"
                              data-testid={`rota-completed-${a.kind}-${a.id}`}
                            >
                              <Tag tone={ALLOCATION_TONES[a.kind]} style={CATEGORY_TAG}>
                                {ALLOCATION_LABELS[a.kind]}
                              </Tag>
                              <Text style={ROW_TEXT}>{allocationLabel(a)}</Text>
                              <Text color="secondary" style={{ flexShrink: 0 }}>
                                {formatCompletedAt(a.completedAt)}
                              </Text>
                            </Stack>
                          ))}
                        </Stack>
                      </div>
                    ))}
                  </Stack>
                ),
            },
          ]}
        />
      </div>

      <ManageStaffDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slots={slots}
        receptionStaff={receptionStaff}
        roster={roster}
        onChanged={load}
      />

      <AddWorkModal target={addWork} onClose={() => setAddWork(null)} onSaved={load} />

      {/*
        FORT-370. Deliberately a one-field modal: cover is "who, on this date", and the
        date is already fixed by which cell was clicked. Anything more would invite it to
        be mistaken for editing the rota itself.
      */}
      <Modal
        open={covering != null}
        title={
          covering
            ? `Cover ${covering.slot.staffName ?? "an unassigned shift"} — ${dayjs(covering.date).format("ddd D MMM")}`
            : "Cover shift"
        }
        onClose={() => setCovering(null)}
        footer={null}
        data-testid="rota-cover-modal"
      >
        <Stack gap={3}>
          <Text color="secondary">
            This applies to this date only. The rota returns to normal next week on its own.
          </Text>
          <Select
            label="Covered by"
            placeholder="Select staff member"
            options={coverCandidates}
            onChange={(value) => (value == null ? undefined : saveCover(Number(value)))}
            data-testid="rota-cover-staff"
          />
        </Stack>
      </Modal>
    </div>
  );
};

export default WorkAllocationRota;
